import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Typography,
  Button,
  Container,
  CircularProgress,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link as MuiLink,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CycleSidePanel from './CycleSidePanel';
import CycleNotesModule from './CycleNotesModule';
import CycleFilesModule from './CycleFilesModule';
import { useUser } from './UserContext';
import { formatDate } from './utils/dateUtils';

// --- Helper Components ---

// Visual status chip
const StatusChip = ({ status, cicloStatus }) => {
  const displayStatus = cicloStatus && cicloStatus !== 'activo' 
    ? cicloStatus.charAt(0).toUpperCase() + cicloStatus.slice(1) 
    : status;
  
  const statusStyles = {
    'Pendiente': { label: 'Pendiente', color: 'warning' },
    'Aprobada': { label: 'Aprobada', color: 'success' },
    'Rechazada': { label: 'Rechazada', color: 'error' },
    'Cerrada': { label: 'Cerrada', color: 'default' },
    'Cancelado': { label: 'Cancelado', style: { backgroundColor: '#000000', color: 'white' } },
    'Terminado': { label: 'Terminado', style: { backgroundColor: '#FFC107', color: 'black' } },
  };
  
  const styleInfo = statusStyles[displayStatus] || { label: displayStatus, color: 'default' };
  
  return <Chip label={styleInfo.label} color={styleInfo.color} style={styleInfo.style} />;
};

// Family Tree Navigator
const FamilyTree = ({ requisicion, rfqs }) => {
  // Only show children if there's exactly one RFQ
  const singleRfq = rfqs && rfqs.length === 1 ? rfqs[0] : null;
  const singleOc = singleRfq?.orden_compra || null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, flexWrap: 'wrap' }}>
      <Paper elevation={3} sx={{ p: 1, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="subtitle1">
          {requisicion.doc_numb || `REQ-${requisicion.id.substring(0, 6)}`}
        </Typography>
      </Paper>
      {singleRfq && (
        <>
          <ArrowForwardIosIcon sx={{ mx: 1 }} />
          <MuiLink component={Link} to={`/solicitudes_cotizacion/${singleRfq.id}`} sx={{ textDecoration: 'none' }}>
            <Paper elevation={3} sx={{ p: 1, '&:hover': { bgcolor: 'grey.200' } }}>
              <Typography variant="subtitle1">
                {singleRfq.doc_numb || `RFQ-${singleRfq.id.substring(0, 6)}`}
              </Typography>
            </Paper>
          </MuiLink>
        </>
      )}
      {singleOc && (
        <>
          <ArrowForwardIosIcon sx={{ mx: 1 }} />
          <MuiLink component={Link} to={`/ordenes-compra/${singleOc.id}`} sx={{ textDecoration: 'none' }}>
            <Paper elevation={3} sx={{ p: 1, '&:hover': { bgcolor: 'grey.200' } }}>
              <Typography variant="subtitle1">
                {singleOc.doc_numb || `OC-${singleOc.id.substring(0, 6)}`}
              </Typography>
            </Paper>
          </MuiLink>
        </>
      )}
    </Box>
  );
};

// --- Main Component ---

const RequisicionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [requisicion, setRequisicion] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useUser();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);

  const canApprove = user && ['ADMIN', 'SUPERADMIN'].includes(user.role);
  const canCreateRfq = user && ['ADMIN', 'SUPERADMIN'].includes(user.role);

  const fetchRequisicionData = async () => {
    setLoading(true);
    try {
      // Fetch requisicion details (which should include adjuntos)
      const reqRes = await axios.get(`/api/requisiciones/${id}`);
      setRequisicion(reqRes.data);

      // Fetch related RFQs
      if (user && ['ADMIN', 'SUPERADMIN'].includes(user.role)) {
        try {
          const rfqRes = await axios.get(`/api/solicitudes_cotizacion/requisicion/${id}`);
          setRfqs(rfqRes.data);
        } catch (err) {
          console.error('Could not fetch RFQs, but continuing.', err);
        }
      }
      setError('');
    } catch (err) {
      setError('No se pudo cargar la información de la requisición.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequisicionData();
  }, [id, user]);

  const handleUpdateStatus = async (newStatus) => {
    try {
      const response = await axios.put(`/api/requisiciones/${id}`, { estado: newStatus });
      setRequisicion(response.data);
    } catch (err) {
      setError(`No se pudo actualizar el estado.`);
      console.error(err);
    }
  };

  const handleCancel = async () => {
    try {
      await axios.put(`/api/requisiciones/${id}/status`, {
        status: 'cancelada',
      });
      navigate('/requisiciones');
    } catch (err) {
      setError('No se pudo cancelar la requisición.');
      console.error(err);
    }
  };


  const handleOpenCancelDialog = () => {
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
  }

  if (error && !requisicion) {
    return <Typography color="error" align="center">{error}</Typography>;
  }

  if (!requisicion) {
    return <Typography align="center">No se encontró la requisición.</Typography>;
  }

  const isActionable = requisicion.status_ciclo === 'activo';

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Family Tree Navigator */}
      <FamilyTree requisicion={requisicion} rfqs={rfqs} />

      <Grid container spacing={3}>
        {/* Main Content (Left) */}
        <Grid item xs={12} md={8}>
          {/* Items Table */}
          <Card>
            <CardHeader title="Artículos de la Requisición" />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Artículo</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Unidad</TableCell>
                    <TableCell align="right">Precio</TableCell>
                    <TableCell align="right">Moneda</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requisicion.detalles.map((detalle) => (
                    <TableRow key={detalle.id}>
                      <TableCell>{detalle.articulo}</TableCell>
                      <TableCell align="right">{detalle.cantidad}</TableCell>
                      <TableCell align="right">{detalle.unidad || '-'}</TableCell>
                      <TableCell align="right">
                        {detalle.precio ? (
                          detalle.moneda === 'EUR'
                            ? `€${detalle.precio.toFixed(2)}`
                            : `${detalle.precio.toFixed(2)}`
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">{detalle.moneda || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Taxes and Totals Section */}
            {(requisicion.subtotal || requisicion.total || (requisicion.impuestos && requisicion.impuestos.length > 0)) && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ minWidth: 250 }}>
                  {requisicion.subtotal != null && (
                    <Typography variant="body2" align="right" gutterBottom>
                      <strong>Subtotal:</strong> ${requisicion.subtotal.toFixed(2)}
                    </Typography>
                  )}
                  {requisicion.impuestos && requisicion.impuestos.length > 0 ? (
                    requisicion.impuestos.map((impuesto, index) => (
                      <Typography key={index} variant="body2" align="right" gutterBottom>
                        <strong>{impuesto.nombre} ({impuesto.porcentaje}%):</strong> ${impuesto.monto.toFixed(2)}
                      </Typography>
                    ))
                  ) : (
                    requisicion.iva != null && requisicion.iva > 0 && (
                      <Typography variant="body2" align="right" gutterBottom>
                        <strong>IVA (16%):</strong> ${requisicion.iva.toFixed(2)}
                      </Typography>
                    )
                  )}
                  {requisicion.total != null && (
                    <Typography variant="body1" align="right" sx={{ mt: 1, pt: 1, borderTop: '1px solid #ddd' }}>
                      <strong>Total:</strong> ${requisicion.total.toFixed(2)}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Card>

          {/* Related RFQs */}
          {canCreateRfq && (
            <Card sx={{ mt: 3 }}>
              <CardHeader
                title="Solicitudes de Cotización (RFQs)"
                action={
                  requisicion.estado === 'Aprobada' && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => navigate('/solicitudes_cotizacion/nueva', { state: { requisicionId: requisicion.id, detalles: requisicion.detalles, impuestos: requisicion.impuestos } })}
                    >
                      Crear RFQ
                    </Button>
                  )
                }
              />
              <List>
                {rfqs.length > 0 ? (
                  rfqs.map(rfq => (
                    <ListItem key={rfq.id} button component={Link} to={`/solicitudes_cotizacion/${rfq.id}`}>
                      <ListItemText 
                        primary={`RFQ para ${rfq.proveedor ? rfq.proveedor.nombre_fiscal : 'N/A'}`}
                        secondary={`Estado: ${rfq.estado}`}
                      />
                      <ArrowForwardIosIcon fontSize="small" />
                    </ListItem>
                  ))
                ) : (
                  <Typography sx={{ p: 2, color: 'text.secondary' }}>
                    No se han creado RFQs para esta requisición.
                  </Typography>
                )}
              </List>
            </Card>
          )}
        </Grid>

        {/* Sidebar (Right) */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={3} direction="column">
            {/* Summary Card */}
            <Grid item>
              <Card>
                <CardHeader title="Resumen" />
                <CardContent>
                  <Typography variant="body2" gutterBottom><strong>Empresa:</strong> {requisicion.empresa?.nombre || 'N/A'}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Área:</strong> {requisicion.area || 'N/A'}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Creado por:</strong> {requisicion.creador?.username || 'N/A'}</Typography>
                  <Typography variant="body2" gutterBottom><strong>Fecha:</strong> {formatDate(requisicion.created_at, true)}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}><strong>Estado:</strong></Typography>
                    <StatusChip status={requisicion.estado} cicloStatus={requisicion.status_ciclo} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Actions Card */}
            <Grid item>
              <Card>
                <CardHeader title="Acciones" />
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {isActionable ? (
                      <>
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => navigate(`/requisiciones/editar/${id}`)}
                        >
                          Editar
                        </Button>
                        {canApprove && requisicion.estado !== 'Aprobada' && (
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleUpdateStatus('Aprobada')}
                          >
                            Aprobar
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          onClick={handleOpenCancelDialog}
                          sx={{ backgroundColor: 'black', color: 'white', '&:hover': { backgroundColor: 'grey.800' } }}
                        >
                          Cancelar
                        </Button>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Esta requisición ya no está activa y no se pueden realizar más acciones.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Dialog
        open={openCancelDialog}
        onClose={handleCloseCancelDialog}
      >
        <DialogTitle>Cancelar Requisición</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea cancelar esta requisición? Esto cancelará el ciclo de compra completo, incluyendo todas las solicitudes de cotización y órdenes de compra relacionadas.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>No</Button>
          <Button onClick={() => { handleCancel(); handleCloseCancelDialog(); }} color="error" autoFocus>
            Sí, cancelar todo el ciclo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cycle Side Panel */}
      <CycleSidePanel isOpen={sidePanelOpen} onToggle={() => setSidePanelOpen(!sidePanelOpen)}>
        <CycleNotesModule elementType="requisicion" elementId={id} />
        <Divider sx={{ my: 3 }} />
        <CycleFilesModule elementType="requisicion" elementId={id} />
      </CycleSidePanel>
    </Container>
  );
};

export default RequisicionDetail;